import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
    Video, AlertCircle, History, Plus, Minus, ArrowRight,
} from 'lucide-react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { resolveFunctionsTarget } from '../utils/environmentConfig';
import { trackException, getCurrentTraceId } from '../services/appInsightsService';
import './MeetingLimitsSection.css';
import { ReadOnlyNote } from './ui/ReadOnlyFieldset';

/**
 * MeetingLimitsSection — Admin Settings > Plans. Lets a platform admin edit the
 * participant/language session limits for the two Meeting plans.
 *
 * Sprint 4 of plans/Meeting-Session-Limits-Plan.md, against the Sprint 2 admin write path as
 * actually delivered (228199e34): GET/PUT /platform/plans(/{id}), a top-level `etag`, and PUT
 * failures in the order 400 LIMIT_OUT_OF_RANGE -> 400 REASON_REQUIRED -> 428
 * PLAN_VERSION_REQUIRED -> 412 PLAN_VERSION_CONFLICT, all as `{code, message}`.
 *
 * The flow mirrors the binding mockup exactly (an inline reason field was rejected on
 * review): dirty edits -> a sticky "N unsaved changes" bar (Discard / Review changes) -> a
 * modal "Save meeting limits?" dialog with a diff, a required reason, and Cancel / Save
 * limits. 412 and 428 are both shown INSIDE the dialog, then trigger a reload — a stale
 * precondition guarantees a repeat failure otherwise. Saves run sequentially per changed
 * plan so one plan's failure never masks whether the next was ever attempted.
 */

const PLAN_IDS = ['plan_meeting_v2', 'plan_meeting_pro_v2'];
const PLAN_FALLBACK_NAMES = { plan_meeting_v2: 'Meeting', plan_meeting_pro_v2: 'Meeting Pro' };
const PLANS_PATH = '/platform/plans';

/** Decorative example languages for the chip row — same list as the binding mockup. */
const EXAMPLE_LANGS = [
    ['ko', 'Korean'], ['ja', 'Japanese'], ['en', 'English'], ['es', 'Spanish'], ['zh', 'Chinese'],
    ['fr', 'French'], ['de', 'German'], ['vi', 'Vietnamese'], ['pt', 'Portuguese'], ['th', 'Thai'],
];

/** Stepper clamp bounds. -1 (unlimited) is a valid participants value; languages start at 1. */
const STEPPER_BOUNDS = {
    draftParticipants: { min: -1, max: 300 },
    draftLanguages: { min: 1, max: EXAMPLE_LANGS.length },
};

/** limitChanges[].field is the prefixless path under `limits` on the wire (Sprint 2, S2
 *  review P1). Anything not in this map shows its raw path rather than a blank or a made-up
 *  label. That includes the `perServiceCaps.*` paths A1 deleted from the plan editor
 *  (`aiCallsPerMonth`, `captureTranslatePerMonth`, `audioMinutesPoolPerMonth`,
 *  `documentPagesPoolPerMonth`): old audit rows keep them and must still render. */
const FIELD_LABELS = {
    maxMeetingParticipantsPerSession: 'Participants',
    maxTargetLanguages: 'Languages',
};
const fieldLabel = (field) => FIELD_LABELS[field] || field;

/** null/undefined is a real, meaningful "not authored yet" on the wire — never render the
 *  literal string "null" to an operator. */
const formatLimitValue = (v) => (v === null || v === undefined ? 'not set' : String(v));

/** A leaf audit entry for a non-numeric field (`features.qa.moderationEnabled` and friends)
 *  carries its before/after as TEXT in `fromValue`/`toValue` and leaves the numeric `from`/`to`
 *  pair null, because a boolean has no numeric value. Text first, numeric second — and an
 *  ABSENT text value still falls through to "not set" rather than printing "null", so "was
 *  false" and "was never authored" stay distinguishable in the same table. */
const formatChangeValue = (text, numeric) => (
    text === null || text === undefined ? formatLimitValue(numeric) : String(text)
);

/** The only real production Functions host — everything else (staging default, localhost,
 *  an explicit staging override) is Staging. Derived from the resolved target, never hardcoded
 *  to one value, so a misconfigured environment reads as Staging rather than silently as Prod. */
const isProductionHost = (baseUrl) => {
    try {
        return new URL(baseUrl).hostname.toLowerCase() === 'func.qolsync.ai';
    } catch (e) {
        return false;
    }
};

/** Never send an empty X-Trace-Id — an admin write that arrives uncorrelated cannot be tied
 *  back to its server-side audit event. */
const traceIdHeader = () => getCurrentTraceId() || `adminportal-${Date.now().toString(36)}`;

/** Projects a raw plan payload into editable state. A null participant cap means "unlimited"
 *  on the wire, displayed as -1 so the operator edits the same number the server stores. */
const toPlanState = (raw) => {
    const participants = raw.limits?.maxMeetingParticipantsPerSession ?? -1;
    const languages = raw.limits?.maxTargetLanguages ?? 0;
    return {
        id: raw.id,
        name: raw.name || PLAN_FALLBACK_NAMES[raw.id] || raw.id,
        monthlyPriceCents: typeof raw.monthlyPriceCents === 'number' ? raw.monthlyPriceCents : undefined,
        etag: raw.etag,
        loadedParticipants: participants,
        loadedLanguages: languages,
        draftParticipants: participants,
        draftLanguages: languages,
        limitChanges: Array.isArray(raw.limitChanges) ? raw.limitChanges : [],
    };
};

const isDirty = (plan) => plan.draftParticipants !== plan.loadedParticipants
    || plan.draftLanguages !== plan.loadedLanguages;

/** -1 (unlimited) is never "less" than a finite cap, and a finite cap is always "less" than
 *  unlimited — a naive numeric `<` would call -1 "less than" 5. */
const isLess = (a, b) => {
    if (a === -1) return false;
    if (b === -1) return true;
    return a < b;
};

/** Host chair + one seat per participant, capped visually at 30 with a "+N" overflow label —
 *  purely decorative (aria-hidden), the real number lives in the labelled input above it. */
const renderSeats = (n) => {
    if (n <= 0) {
        return (
            <div className="meeting-limits-seats" aria-hidden="true">
                <span className="meeting-limits-seat meeting-limits-seat-host" />
                <span className="meeting-limits-seat-note">
                    {n === -1 ? 'Unlimited — no join is ever turned away' : 'No one besides the host can join'}
                </span>
            </div>
        );
    }
    const shown = Math.min(n, 30);
    return (
        <div className="meeting-limits-seats" aria-hidden="true">
            <span className="meeting-limits-seat meeting-limits-seat-host" />
            {Array.from({ length: shown }, (_, i) => (
                // eslint-disable-next-line react/no-array-index-key -- decorative filler, no identity
                <span key={i} className="meeting-limits-seat" />
            ))}
            {n > shown && <span className="meeting-limits-seat-note">{`+${n - shown}`}</span>}
        </div>
    );
};

/** Example language chips (same list as the binding mockup), chips past the limit shown
 *  refused. Decorative — the real number lives in the labelled input above it. */
const renderLangChips = (n) => {
    const example = EXAMPLE_LANGS.slice(0, Math.max(n + 1, 3));
    return (
        <div className="meeting-limits-langs" aria-hidden="true">
            {example.map(([code, name], i) => (
                <span
                    key={code}
                    className={`meeting-limits-lang${i >= n ? ' meeting-limits-lang-refused' : ''}`}
                >
                    <code>{code}</code>
                    {name}
                </span>
            ))}
        </div>
    );
};

/** Literally what a refused participant sees, and who: the mockup's fullMessage() plus which
 *  ordinal join it would be. */
const fullMeetingMessage = (n) => {
    if (n < 0) return null;
    const who = n === 0 ? 'shown to the first participant' : `shown to participant ${n + 1}`;
    return `"This session has reached its participant limit (${n}). Please try again later or contact the host." — ${who}`;
};

const FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], input:not([disabled]), '
    + 'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const alertStyle = {
    margin: '0 0 var(--space-4)',
    padding: 'var(--space-2) var(--space-3)',
    borderRadius: 'var(--radius-md)',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--space-2)',
    // --error is the same static hex in both themes, so it cannot serve as TEXT on a
    // background tinted from --bg-card (established pattern, see PlanEditor.js noticeStyle).
    background: 'color-mix(in srgb, var(--error) 12%, var(--bg-card))',
    color: 'var(--text-primary)',
};

const MeetingLimitsSection = ({ readOnly = false }) => {
    const [plans, setPlans] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [loadErrorMessage, setLoadErrorMessage] = useState(null);
    const [dialogError, setDialogError] = useState(null);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [reason, setReason] = useState('');
    const [toast, setToast] = useState(null);

    const dialogRef = useRef(null);
    const reasonRef = useRef(null);
    const reviewBtnRef = useRef(null);
    const toastTimerRef = useRef(null);
    // The section's own content, and its change-history table — made `inert` (not unmounted,
    // not `display:none`) while the dialog is open. See the effect below.
    const backgroundRef = useRef(null);
    const historyRef = useRef(null);
    // Deferred so a `.focus()` at close time never races the re-render that re-enables the
    // Review button (it is `inert` while the dialog is open, not unmounted, but a disabled/inert
    // element cannot receive focus until the attribute is actually removed from the DOM).
    const pendingFocusReturnRef = useRef(false);

    const target = useMemo(() => resolveFunctionsTarget(
        process.env.REACT_APP_FUNCTIONS_URL,
        process.env.REACT_APP_WEBSOCKET_URL,
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1',
    ), []);
    const envLabel = isProductionHost(target.baseUrl) ? 'Production' : 'Staging';

    const load = useCallback(async () => {
        // An unresolved target is the hidden staging fallback, not a deliberate backend — do not
        // send an authenticated GET to it (see RealtimeProviderPolicySettings.js's identical guard).
        if (!target.resolved) return;

        setIsLoading(true);
        try {
            const response = await fetchWithAuth(`${target.baseUrl}${PLANS_PATH}`, {
                method: 'GET',
                headers: { 'X-Trace-Id': traceIdHeader() },
            });
            if (!response.ok) {
                throw new Error(`Failed to load meeting session limits (HTTP ${response.status}).`);
            }
            const data = await response.json();
            const list = Array.isArray(data) ? data : (data.plans || []);
            const byId = {};
            list.forEach((raw) => {
                if (PLAN_IDS.includes(raw.id)) byId[raw.id] = toPlanState(raw);
            });
            setPlans(byId);
        } catch (err) {
            trackException(err, { errorType: 'MeetingLimits_LoadFailed' });
            setLoadErrorMessage(err.message || 'Failed to load meeting session limits.');
        } finally {
            setIsLoading(false);
        }
    }, [target.baseUrl, target.resolved]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!reviewOpen && pendingFocusReturnRef.current) {
            pendingFocusReturnRef.current = false;
            reviewBtnRef.current?.focus();
        }
    }, [reviewOpen]);

    // `inert` set imperatively, not as a JSX boolean prop: React 18 does not recognize it as a
    // modeled DOM property (unlike `aria-hidden`), so `setAttribute`/`removeAttribute` is the
    // unambiguous way to apply the actual boolean HTML attribute in every React 18 renderer.
    useEffect(() => {
        [backgroundRef.current, historyRef.current].forEach((el) => {
            if (!el) return;
            if (reviewOpen) {
                el.setAttribute('inert', '');
            } else {
                el.removeAttribute('inert');
            }
        });
    }, [reviewOpen]);

    useEffect(() => () => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    }, []);

    // Focus moves into the dialog the moment it mounts — never left on whatever was focused
    // in the page behind it.
    useEffect(() => {
        if (reviewOpen) reasonRef.current?.focus();
    }, [reviewOpen]);

    const updateDraft = (planId, field, rawValue) => {
        const parsed = parseInt(rawValue, 10);
        setPlans((prev) => ({
            ...prev,
            [planId]: {
                ...prev[planId],
                [field]: Number.isNaN(parsed) ? prev[planId][field] : parsed,
            },
        }));
    };

    // Stepper buttons around the same labelled input — clamps to STEPPER_BOUNDS so +/- can't
    // walk the draft to a value the server would reject anyway.
    const adjustDraft = (planId, field, delta) => {
        const { min, max } = STEPPER_BOUNDS[field];
        setPlans((prev) => {
            const next = Math.max(min, Math.min(max, prev[planId][field] + delta));
            return { ...prev, [planId]: { ...prev[planId], [field]: next } };
        });
    };

    const dirtyIds = plans ? PLAN_IDS.filter((id) => plans[id] && isDirty(plans[id])) : [];
    const anyDirty = dirtyIds.length > 0;
    const reasonValid = reason.trim().length >= 5;

    /** One row per changed FIELD (not per plan) — a plan with both limits touched contributes
     *  two rows, matching the mockup's per-field diff/count. */
    const changedFields = () => {
        const out = [];
        dirtyIds.forEach((planId) => {
            const plan = plans[planId];
            if (plan.draftParticipants !== plan.loadedParticipants) {
                out.push({
                    planId,
                    planName: plan.name,
                    field: 'maxMeetingParticipantsPerSession',
                    label: fieldLabel('maxMeetingParticipantsPerSession'),
                    from: plan.loadedParticipants,
                    to: plan.draftParticipants,
                });
            }
            if (plan.draftLanguages !== plan.loadedLanguages) {
                out.push({
                    planId,
                    planName: plan.name,
                    field: 'maxTargetLanguages',
                    label: fieldLabel('maxTargetLanguages'),
                    from: plan.loadedLanguages,
                    to: plan.draftLanguages,
                });
            }
        });
        return out;
    };

    const closeDialog = (returnFocus) => {
        pendingFocusReturnRef.current = returnFocus;
        setReviewOpen(false);
        setDialogError(null);
    };

    const handleDiscard = () => {
        setPlans((prev) => {
            const next = { ...prev };
            PLAN_IDS.forEach((id) => {
                if (next[id]) {
                    next[id] = { ...next[id], draftParticipants: next[id].loadedParticipants, draftLanguages: next[id].loadedLanguages };
                }
            });
            return next;
        });
        setReason('');
        closeDialog(false);
    };

    const handleDialogKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            closeDialog(true);
            return;
        }
        if (e.key !== 'Tab' || !dialogRef.current) return;
        const focusable = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR));
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    };

    const showToast = (message) => {
        setToast(message);
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        toastTimerRef.current = setTimeout(() => setToast(null), 2400);
    };

    const handleSave = async () => {
        if (readOnly) return;
        if (!plans || !anyDirty || !reasonValid || !target.resolved || isSaving) return;

        const reasonToSend = reason.trim();
        setDialogError(null);
        setIsSaving(true);
        try {
            for (const planId of dirtyIds) {
                const plan = plans[planId];
                // eslint-disable-next-line no-await-in-loop -- must run sequentially: stop the
                // loop on the first plan's failure rather than attempting the next plan's PUT.
                const response = await fetchWithAuth(`${target.baseUrl}${PLANS_PATH}/${encodeURIComponent(planId)}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'If-Match': plan.etag,
                        'X-Trace-Id': traceIdHeader(),
                    },
                    body: JSON.stringify({
                        limits: {
                            maxMeetingParticipantsPerSession: plan.draftParticipants,
                            maxTargetLanguages: plan.draftLanguages,
                        },
                        reason: reasonToSend,
                    }),
                });

                if (response.status === 412 || response.status === 428) {
                    // eslint-disable-next-line no-await-in-loop
                    const body = await response.json().catch(() => ({}));
                    const fallback = response.status === 412
                        ? `Someone else already saved a change to ${plan.name} (version conflict). Reloading the latest values.`
                        : `${plan.name} needs the latest saved version before this can be applied. Reloading, then try again.`;
                    const message = body.message || fallback;
                    trackException(new Error(message), {
                        errorType: 'MeetingLimits_SaveConflict', planId, code: body.code,
                    });
                    setDialogError(message);
                    // eslint-disable-next-line no-await-in-loop -- the stale precondition
                    // guarantees a repeat failure, so the fresh values must be reloaded first.
                    await load();
                    return;
                }

                if (!response.ok) {
                    // eslint-disable-next-line no-await-in-loop
                    const body = await response.json().catch(() => ({}));
                    const message = body.message || `Failed to save ${plan.name} (HTTP ${response.status}).`;
                    trackException(new Error(message), {
                        errorType: 'MeetingLimits_SaveFailed', planId, code: body.code,
                    });
                    setDialogError(message);
                    return;
                }

                // eslint-disable-next-line no-await-in-loop
                const saved = await response.json();
                setPlans((prev) => ({ ...prev, [planId]: toPlanState(saved) }));
            }
            setReason('');
            showToast('Limits saved');
            closeDialog(true);
        } catch (err) {
            trackException(err, { errorType: 'MeetingLimits_SaveThrew' });
            setDialogError(err.message || 'Failed to save meeting session limits.');
        } finally {
            setIsSaving(false);
        }
    };

    const historyRows = plans
        ? PLAN_IDS
            // `load()` builds `byId` from only the ids the server actually returned, so a
            // `/platform/plans` response that omits a plan — an empty list included — is a real
            // response that leaves `plans` a truthy partial object. This was the one remaining
            // unguarded dereference of `plans[id]`; the plan-card path below already skips a
            // missing plan with `if (!plan) return null;`. It matters more here than there:
            // `historyRows` is computed before any JSX exists and this route carries no
            // ErrorBoundary, so a throw took the whole Plans tab down, not just this table.
            .filter((id) => plans[id])
            .flatMap((id) => (plans[id].limitChanges || []).map((change) => ({ ...change, planName: plans[id].name })))
            .sort((a, b) => new Date(b.atUtc) - new Date(a.atUtc))
        : [];

    const changeCount = anyDirty ? changedFields().length : 0;

    return (
        <section className="meeting-limits-section" aria-labelledby="meeting-limits-title">
            {/* `inert` + `aria-hidden`, NOT `display:none`/`visibility:hidden`, while the dialog
                is open. A `display:none` predecessor here removed this whole subtree from
                layout, which reflowed AdminSettings' own content up into its place — an admin
                scrolled to this section watched the page shift under the dialog on open and
                again on close. `inert` makes the background genuinely non-interactive and (per
                spec) hidden from assistive tech without touching layout at all; `aria-hidden` is
                kept alongside it defensively. `inert` is set imperatively via a ref rather than
                as a bare JSX boolean prop: React 18 does not model it as a recognized DOM
                property, so passing it as a literal `boolean` can serialize inconsistently
                across renderers — `setAttribute`/`removeAttribute` is unambiguous. */}
            <div ref={backgroundRef} aria-hidden={reviewOpen || undefined} data-testid="meeting-limits-background">
            <div className="meeting-limits-header">
                <div className="meeting-limits-header-left">
                    <Video size={20} />
                    <div>
                        <h2 id="meeting-limits-title">Meeting session limits</h2>
                        <p>How many people can join one translated meeting, and how many languages it can run.</p>
                    </div>
                </div>
                <span className="meeting-limits-env-chip" title="Changes apply to this environment only">
                    {envLabel}
                </span>
            </div>

            {loadErrorMessage && (
                <div role="alert" style={alertStyle}>
                    <AlertCircle size={16} />
                    <span>{loadErrorMessage}</span>
                </div>
            )}

            <ReadOnlyNote readOnly={readOnly} />

            {isLoading && !plans && <p className="meeting-limits-status">Loading meeting session limits…</p>}

            {plans && (
                <div className="meeting-limits-plans">
                    {PLAN_IDS.map((planId) => {
                        const plan = plans[planId];
                        if (!plan) return null;
                        const dirty = isDirty(plan);
                        // Every field and stepper; a platform reader (level 3) cannot write plans.
                        const disabled = readOnly || !target.resolved || isSaving;
                        const isPro = planId === PLAN_IDS[1];
                        const meetingPlan = plans[PLAN_IDS[0]];
                        const showsLessThanWarning = isPro && meetingPlan && (
                            isLess(plan.draftParticipants, meetingPlan.draftParticipants)
                            || isLess(plan.draftLanguages, meetingPlan.draftLanguages)
                        );
                        return (
                            <div
                                key={planId}
                                className={`meeting-limits-plan${dirty ? ' meeting-limits-plan-changed' : ''}`}
                                data-testid={`meeting-limits-plan-${planId}`}
                            >
                                <div className="meeting-limits-plan-title-row">
                                    <h3 className="meeting-limits-plan-name">{plan.name}</h3>
                                    {typeof plan.monthlyPriceCents === 'number' && (
                                        <span className="meeting-limits-plan-price">
                                            {`$${(plan.monthlyPriceCents / 100).toFixed(2)}/mo`}
                                        </span>
                                    )}
                                </div>
                                <div className="meeting-limits-plan-id">{plan.id}</div>

                                <div className="meeting-limits-field">
                                    <label htmlFor={`participants-${planId}`}>Participants per meeting</label>
                                    <span className="meeting-limits-hint">Besides the host. -1 means unlimited.</span>
                                    <div className="meeting-limits-stepper">
                                        <button
                                            type="button"
                                            aria-label={`Decrease join cap for ${plan.name}`}
                                            onClick={() => adjustDraft(planId, 'draftParticipants', -1)}
                                            disabled={disabled
                                                || plan.draftParticipants <= STEPPER_BOUNDS.draftParticipants.min}
                                        >
                                            <Minus size={14} />
                                        </button>
                                        <input
                                            id={`participants-${planId}`}
                                            type="number"
                                            value={plan.draftParticipants}
                                            onChange={(e) => updateDraft(planId, 'draftParticipants', e.target.value)}
                                            disabled={disabled}
                                        />
                                        <button
                                            type="button"
                                            aria-label={`Increase join cap for ${plan.name}`}
                                            onClick={() => adjustDraft(planId, 'draftParticipants', 1)}
                                            disabled={disabled
                                                || plan.draftParticipants >= STEPPER_BOUNDS.draftParticipants.max}
                                        >
                                            <Plus size={14} />
                                        </button>
                                    </div>
                                    {renderSeats(plan.draftParticipants)}
                                    {fullMeetingMessage(plan.draftParticipants) && (
                                        <div className="meeting-limits-preview">
                                            <h4>What a refused participant sees</h4>
                                            <blockquote>{fullMeetingMessage(plan.draftParticipants)}</blockquote>
                                        </div>
                                    )}
                                </div>

                                <div className="meeting-limits-field">
                                    <label htmlFor={`languages-${planId}`}>Languages per meeting</label>
                                    <span className="meeting-limits-hint">Each person reads one.</span>
                                    <div className="meeting-limits-stepper">
                                        <button
                                            type="button"
                                            aria-label={`Decrease language cap for ${plan.name}`}
                                            onClick={() => adjustDraft(planId, 'draftLanguages', -1)}
                                            disabled={disabled
                                                || plan.draftLanguages <= STEPPER_BOUNDS.draftLanguages.min}
                                        >
                                            <Minus size={14} />
                                        </button>
                                        <input
                                            id={`languages-${planId}`}
                                            type="number"
                                            value={plan.draftLanguages}
                                            onChange={(e) => updateDraft(planId, 'draftLanguages', e.target.value)}
                                            disabled={disabled}
                                        />
                                        <button
                                            type="button"
                                            aria-label={`Increase language cap for ${plan.name}`}
                                            onClick={() => adjustDraft(planId, 'draftLanguages', 1)}
                                            disabled={disabled
                                                || plan.draftLanguages >= STEPPER_BOUNDS.draftLanguages.max}
                                        >
                                            <Plus size={14} />
                                        </button>
                                    </div>
                                    <div
                                        className="meeting-limits-langs-wrap"
                                        title="Example: languages people might pick, in order"
                                    >
                                        {renderLangChips(plan.draftLanguages)}
                                    </div>
                                </div>

                                {showsLessThanWarning && (
                                    <div className="meeting-limits-warning" role="note">
                                        <AlertCircle size={14} />
                                        <span>Meeting Pro now allows less than Meeting. Upgrading would take something away.</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {plans && (
                <div className="meeting-limits-applies">
                    <div>
                        <strong>When it applies</strong>
                        <span>The next person who joins, and the next language someone picks.</span>
                    </div>
                    <div>
                        <strong>Meetings in progress</strong>
                        <span>People and languages already in the room stay. Only new joins are checked.</span>
                    </div>
                    <div>
                        <strong>Who counts</strong>
                        <span>The host and co-hosts never count toward the participant limit.</span>
                    </div>
                </div>
            )}

            {anyDirty && (
                // Stays in the DOM and in layout the whole time — `inert` on the shared
                // `backgroundRef` ancestor above (not a style on this element) is what makes it
                // non-interactive while the dialog is open. The Review button therefore keeps
                // its DOM identity throughout, so focus can return to the SAME node on close.
                <div
                    className="meeting-limits-savebar"
                    role="region"
                    aria-label="Unsaved changes"
                >
                    <p className="meeting-limits-savebar-summary">
                        {`${changeCount} unsaved ${changeCount === 1 ? 'change' : 'changes'} in ${envLabel}`}
                    </p>
                    <div className="meeting-limits-savebar-actions">
                        <button type="button" className="meeting-limits-discard-btn" onClick={handleDiscard} disabled={isSaving}>
                            Discard
                        </button>
                        <button
                            type="button"
                            className="meeting-limits-review-btn"
                            ref={reviewBtnRef}
                            onClick={() => setReviewOpen(true)}
                            disabled={isSaving}
                        >
                            Review changes
                        </button>
                    </div>
                </div>
            )}
            </div>

            {reviewOpen && (
                <div
                    className="meeting-limits-overlay"
                    onMouseDown={(e) => { if (e.target === e.currentTarget) closeDialog(true); }}
                >
                    <div
                        className="meeting-limits-dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="meeting-limits-dialog-title"
                        ref={dialogRef}
                        onKeyDown={handleDialogKeyDown}
                    >
                        <h2 id="meeting-limits-dialog-title">Save meeting limits?</h2>
                        <p>{`These limits apply to every tenant on these plans in ${envLabel}, starting with the next join.`}</p>

                        <div className="meeting-limits-diff">
                            {changedFields().map((c) => (
                                <div className="meeting-limits-diff-row" key={`${c.planId}-${c.field}`}>
                                    <span className="meeting-limits-diff-what">{`${c.planName} · ${c.label}`}</span>
                                    {/* No bare text node directly under .meeting-limits-diff-vals — axe's
                                        color-contrast check flags a symbol-only direct text run ("→") as
                                        "content contains only non-text characters" and cannot resolve the
                                        element's own contrast, even though its <s>/<b> children are each
                                        individually fine. Wrapping the arrow in its own span gives every
                                        direct child real text axe can measure. */}
                                    <span className="meeting-limits-diff-vals">
                                        <s>{formatLimitValue(c.from)}</s>
                                        {/* An SVG icon, not a "→" text glyph: axe's color-contrast check
                                            cannot evaluate ANY element whose only content is a bare arrow
                                            character ("content contains only non-text characters") and
                                            aria-hidden does not suppress that when axe is run scoped to a
                                            specific node rather than the whole document. An icon carries
                                            no text node at all, so there is nothing left for that check to
                                            (fail to) evaluate. Decorative only — <s>/<b> already carry the
                                            real old/new values, in that visual order. */}
                                        <ArrowRight className="meeting-limits-diff-arrow" size={12} aria-hidden="true" />
                                        <b>{formatLimitValue(c.to)}</b>
                                    </span>
                                </div>
                            ))}
                        </div>

                        {dialogError && (
                            <div role="alert" style={alertStyle}>
                                <AlertCircle size={16} />
                                <span>{dialogError}</span>
                            </div>
                        )}

                        <div className="meeting-limits-reason">
                            <label htmlFor="meeting-limits-reason">Reason for the change</label>
                            <textarea
                                id="meeting-limits-reason"
                                ref={reasonRef}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Example: raising the participant cap for an enterprise pilot."
                                disabled={isSaving}
                            />
                            <small>Saved to the change history with your name.</small>
                        </div>

                        <div className="meeting-limits-dialog-actions">
                            <button type="button" onClick={() => closeDialog(true)} disabled={isSaving}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="meeting-limits-save-btn"
                                onClick={handleSave}
                                disabled={!target.resolved || isSaving || !reasonValid}
                            >
                                {isSaving ? 'Saving…' : 'Save limits'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {toast && (
                <div className="meeting-limits-toast" role="status">{toast}</div>
            )}

            {historyRows.length > 0 && (
                <div
                    className="meeting-limits-history"
                    ref={historyRef}
                    aria-hidden={reviewOpen || undefined}
                >
                    <h3><History size={16} /> Change history</h3>
                    <div className="meeting-limits-table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>When</th>
                                    <th>Plan</th>
                                    <th>Field</th>
                                    <th>From</th>
                                    <th>To</th>
                                    <th>Reason</th>
                                </tr>
                            </thead>
                            <tbody>
                                {historyRows.map((row, index) => (
                                    // eslint-disable-next-line react/no-array-index-key -- change
                                    // history rows carry no stable id on the wire.
                                    <tr key={`${row.planName}-${row.field}-${index}`}>
                                        <td>{row.atUtc ? new Date(row.atUtc).toLocaleString() : '—'}</td>
                                        <td>{row.planName}</td>
                                        <td title={row.field}>{fieldLabel(row.field)}</td>
                                        <td>{formatChangeValue(row.fromValue, row.from)}</td>
                                        <td>{formatChangeValue(row.toValue, row.to)}</td>
                                        <td>{row.reason}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </section>
    );
};

export default MeetingLimitsSection;
